import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, Users, ChevronRight, Mail, Phone,
  MapPin, Building2, MoreVertical, Edit, Eye, Star,
  Package, Wrench, ShieldCheck, Calendar, Download
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const statusColors = {
  active: 'bg-[#142915] text-[#00E01A] border-[#00E01A]/20',
  vip: 'bg-[#2A2010] text-[#F39C12] border-[#F39C12]/20',
  inactive: 'bg-[#2C3338] text-[#8C8F94] border-[#8C8F94]/20'
};

const statusLabels = {
  active: 'Actif',
  vip: 'VIP',
  inactive: 'Inactif'
};

const ClientForm = ({ client, franchises, onSave, onClose }) => {
  const [formData, setFormData] = useState(client || {
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    source: '',
    franchise_id: franchises[0]?.id || '',
    status: 'active'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">Prénom *</Label>
          <Input
            id="first_name"
            value={formData.first_name}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            required
            data-testid="client-first-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">Nom *</Label>
          <Input
            id="last_name"
            value={formData.last_name}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            required
            data-testid="client-last-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            data-testid="client-email-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Téléphone</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            data-testid="client-phone-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company">Entreprise</Label>
          <Input
            id="company"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            data-testid="client-company-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="franchise">Franchise *</Label>
          <Select
            value={formData.franchise_id}
            onValueChange={(value) => setFormData({ ...formData, franchise_id: value })}
          >
            <SelectTrigger data-testid="client-franchise-select">
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent>
              {franchises.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="address">Adresse</Label>
          <Input
            id="address"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            data-testid="client-address-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="city">Ville</Label>
          <Input
            id="city"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            data-testid="client-city-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="postal_code">Code postal</Label>
          <Input
            id="postal_code"
            value={formData.postal_code}
            onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
            data-testid="client-postal-code-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="source">Source</Label>
          <Select
            value={formData.source || ''}
            onValueChange={(value) => setFormData({ ...formData, source: value })}
          >
            <SelectTrigger data-testid="client-source-select">
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="website">Site web</SelectItem>
              <SelectItem value="salon">Salon</SelectItem>
              <SelectItem value="referral">Recommandation</SelectItem>
              <SelectItem value="social">Réseaux sociaux</SelectItem>
              <SelectItem value="other">Autre</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="status">Statut</Label>
          <Select
            value={formData.status}
            onValueChange={(value) => setFormData({ ...formData, status: value })}
          >
            <SelectTrigger data-testid="client-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Actif</SelectItem>
              <SelectItem value="vip">VIP</SelectItem>
              <SelectItem value="inactive">Inactif</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button type="button" variant="outline" onClick={onClose} data-testid="client-cancel-btn">
          Annuler
        </Button>
        <Button type="submit" className="bg-[#00E01A] hover:bg-[#00B815] text-black" data-testid="client-save-btn">
          {client ? 'Mettre à jour' : 'Créer'}
        </Button>
      </div>
    </form>
  );
};

const ClientCard = ({ client }) => (
  <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 hover:border-[#00E01A]/30 transition-all duration-200 group">
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#141617] rounded-full flex items-center justify-center text-[#00E01A] font-semibold">
          {client.first_name?.[0]}{client.last_name?.[0]}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-white font-semibold">{client.full_name}</h3>
            {client.status === 'vip' && <Star size={14} className="text-[#F39C12]" />}
          </div>
          {client.company && (
            <p className="text-[#8C8F94] text-sm">{client.company}</p>
          )}
        </div>
      </div>
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusColors[client.status]}`}>
        {statusLabels[client.status]}
      </span>
      {client.source === 'exploitation' && (
        <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-[#3B82F6]/15 text-[#3B82F6] border border-[#3B82F6]/20">
          Location
        </span>
      )}
    </div>

    <div className="space-y-2 mb-3">
      {client.email && (
        <div className="flex items-center gap-2 text-[#8C8F94] text-sm">
          <Mail size={14} />
          <span className="truncate">{client.email}</span>
        </div>
      )}
      {client.phone && (
        <div className="flex items-center gap-2 text-[#8C8F94] text-sm">
          <Phone size={14} />
          <span>{client.phone}</span>
        </div>
      )}
      {client.city && (
        <div className="flex items-center gap-2 text-[#8C8F94] text-sm">
          <MapPin size={14} />
          <span>{client.city}</span>
        </div>
      )}
    </div>

    <div className="grid grid-cols-3 gap-2 mb-3">
      <div className="bg-[#141617] rounded-lg p-2 text-center">
        <p className="text-white font-bold">{client.equipment_count}</p>
        <p className="text-[#8C8F94] text-xs">Équip.</p>
      </div>
      <div className="bg-[#141617] rounded-lg p-2 text-center">
        <p className="text-white font-bold">{client.sav_count}</p>
        <p className="text-[#8C8F94] text-xs">SAV</p>
      </div>
      <div className="bg-[#141617] rounded-lg p-2 text-center">
        <p className="text-[#00E01A] font-bold text-sm">
          {client.total_ca ? `${(client.total_ca / 1000).toFixed(0)}k€` : '0€'}
        </p>
        <p className="text-[#8C8F94] text-xs">CA</p>
      </div>
    </div>

    <Link
      to={`/clients/${client.id}`}
      className="flex items-center justify-between text-sm text-[#8C8F94] group-hover:text-[#00E01A] transition-colors pt-3 border-t border-[#2C3338]"
      data-testid={`client-link-${client.id}`}
    >
      <span>Voir le passeport</span>
      <ChevronRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
    </Link>
  </div>
);

const Clients = () => {
  const { api, user, isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [franchiseFilter, setFranchiseFilter] = useState(searchParams.get('franchise_id') || 'all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);

  const fetchData = async () => {
    try {
      const [clientsRes, franchisesRes] = await Promise.all([
        api.get('/clients'),
        api.get('/franchises')
      ]);
      setClients(clientsRes.data);
      setFranchises(franchisesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  const handleSave = async (data) => {
    try {
      await api.post('/clients', data);
      toast.success('Client créé');
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to save client:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleImportExploitation = async () => {
    setImporting(true);
    try {
      const res = await api.post('/clients/import-exploitation');
      toast.success(res.data.message);
      if (res.data.imported > 0) fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const filteredClients = clients.filter(c => {
    const q = search.toLowerCase();
    const matchesSearch = !q ||
      c.full_name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.company?.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q) ||
      c.city?.toLowerCase().includes(q) ||
      c.postal_code?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    const matchesFranchise = franchiseFilter === 'all' || c.franchise_id === franchiseFilter;
    return matchesSearch && matchesStatus && matchesFranchise;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#2C3338] rounded-full"></div>
                <div>
                  <div className="h-4 w-24 bg-[#2C3338] rounded mb-1"></div>
                  <div className="h-3 w-16 bg-[#2C3338] rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
            Clients
          </h1>
          <p className="text-[#8C8F94] mt-1">
            {clients.length} client{clients.length > 1 ? 's' : ''} enregistré{clients.length > 1 ? 's' : ''}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleImportExploitation}
            disabled={importing}
            className="flex items-center gap-2 bg-[#1D2327] hover:bg-[#2C3338] text-white border border-[#2C3338] font-medium px-4 py-2.5 rounded-full transition-all text-sm disabled:opacity-50"
            data-testid="import-exploitation-btn"
          >
            <Download size={16} className={importing ? 'animate-pulse' : ''} />
            <span className="hidden md:inline">{importing ? 'Import...' : 'Importer Exploitation'}</span>
          </button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)] hover:shadow-[0_0_25px_rgba(0,224,26,0.4)]"
                data-testid="add-client-btn"
              >
                <Plus size={18} />
                <span className="hidden md:inline">Ajouter</span>
              </button>
            </DialogTrigger>
          <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Outfit']">
                Nouveau client
              </DialogTitle>
            </DialogHeader>
            <ClientForm
              franchises={franchises}
              onSave={handleSave}
              onClose={() => setDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8F94]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email, téléphone, ville..."
            className="w-full bg-[#1D2327] border border-[#2C3338] rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-[#52555A] focus:outline-none focus:border-[#00E01A] transition-colors"
            data-testid="client-search-input"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-40 bg-[#1D2327] border-[#2C3338]" data-testid="client-status-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-[#1D2327] border-[#2C3338]">
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="active">Actifs</SelectItem>
            <SelectItem value="vip">VIP</SelectItem>
            <SelectItem value="inactive">Inactifs</SelectItem>
          </SelectContent>
        </Select>

        {isSuperAdmin() && (
          <Select value={franchiseFilter} onValueChange={setFranchiseFilter}>
            <SelectTrigger className="w-full md:w-48 bg-[#1D2327] border-[#2C3338]" data-testid="client-franchise-filter">
              <SelectValue placeholder="Franchise" />
            </SelectTrigger>
            <SelectContent className="bg-[#1D2327] border-[#2C3338]">
              <SelectItem value="all">Toutes franchises</SelectItem>
              {franchises.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <div className="text-center py-12">
          <Users size={48} className="mx-auto text-[#8C8F94] mb-4" />
          <p className="text-[#8C8F94]">
            {search || statusFilter !== 'all' || franchiseFilter !== 'all' 
              ? 'Aucun client trouvé' 
              : 'Aucun client enregistré'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <ClientCard key={client.id} client={client} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Clients;
